import React, { useState, useRef, useEffect } from "react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_KEY = "safuaye_chat_session";

/** Chatbot flottant Assistant SAFUAYE — visible sur toutes les pages publiques */
export default function ChatbotWidget() {
  const [open, setOpen] = useState(false);
  // Hide the launcher when the footer (or any element with id="site-footer") is
  // in view, so it never covers the small ⚙️ admin link located in the footer.
  const [hidden, setHidden] = useState(false);
  useEffect(() => {
    const el = document.getElementById("site-footer");
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      (entries) => {
        const e = entries[0];
        // If more than 5% of the footer is visible → hide the widget launcher
        setHidden(e.isIntersecting && e.intersectionRatio > 0.05);
      },
      { threshold: [0, 0.05, 0.5, 1] }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  const [messages, setMessages] = useState([
    { role: "assistant",
      text: "Bonjour 🌿 Je suis l'Assistant SAFUAYE. Je peux vous parler de l'association, de l'adhésion à 3 000 FCFA/an, de notre Académie de Football gratuite (U8-U13), du Gala du 8 septembre 2026, ou vous aider à faire un don. En quoi puis-je vous être utile ?" },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId] = useState(() => {
    let s = localStorage.getItem(SESSION_KEY);
    if (!s) {
      s = `web-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      localStorage.setItem(SESSION_KEY, s);
    }
    return s;
  });
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text }]);
    setBusy(true);
    try {
      const r = await fetch(`${API}/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: text, lang: "fr" }),
      });
      const d = await r.json();
      if (r.ok) {
        setMessages((m) => [...m, { role: "assistant", text: d.reply || "…" }]);
      } else {
        setMessages((m) => [...m, { role: "assistant", text: "❌ Une erreur est survenue. Merci de réessayer ou d'écrire à associationsafuaye@gmail.com." }]);
      }
    } catch {
      setMessages((m) => [...m, { role: "assistant", text: "❌ Connexion impossible. Réessayez dans un instant." }]);
    } finally { setBusy(false); }
  };

  return (
    <>
      {/* Bouton flottant */}
      {!open && !hidden && (
        <button
          onClick={() => setOpen(true)}
          data-testid="chatbot-open-btn"
          className="fixed bottom-6 right-6 z-40 bg-[#0B2545] hover:bg-[#08192F] text-white rounded-full shadow-2xl p-4 md:p-5 flex items-center gap-2 transition-all hover:scale-105 border-2 border-amber-400"
          aria-label="Ouvrir l'assistant SAFUAYE"
        >
          <span className="text-2xl">💬</span>
          <span className="hidden md:inline text-sm font-semibold">Assistant SAFUAYE</span>
        </button>
      )}

      {/* Fenêtre chat */}
      {open && (
        <div
          data-testid="chatbot-window"
          className="fixed bottom-6 right-6 z-50 w-[92vw] md:w-[400px] h-[75vh] md:h-[560px] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden border-2 border-[#0B2545]"
        >
          {/* Header */}
          <div className="bg-[#0B2545] text-white p-4 flex items-center justify-between">
            <div>
              <div className="font-bold text-sm">💬 Assistant SAFUAYE</div>
              <div className="text-xs text-amber-300 italic">Propulsé par Gemini · Réponses en FR/EN/IT</div>
            </div>
            <button
              onClick={() => setOpen(false)}
              data-testid="chatbot-close-btn"
              className="text-white hover:text-amber-300 text-2xl leading-none"
              aria-label="Fermer"
            >×</button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-2 bg-gray-50" data-testid="chatbot-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                  m.role === "user"
                    ? "bg-amber-500 text-white rounded-br-sm"
                    : "bg-white text-gray-800 border border-gray-200 rounded-bl-sm shadow-sm"
                }`}>
                  {m.text}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex justify-start">
                <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-sm px-3 py-2 shadow-sm">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" />
                    <div className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{animationDelay: "0.15s"}} />
                    <div className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{animationDelay: "0.3s"}} />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="border-t border-gray-200 p-3 bg-white flex gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Écrivez votre question…"
              disabled={busy}
              data-testid="chatbot-input"
              className="flex-1 border border-gray-300 rounded-full px-4 py-2 text-sm focus:border-[#0B2545] focus:outline-none disabled:bg-gray-100"
            />
            <button
              onClick={send}
              disabled={busy || !input.trim()}
              data-testid="chatbot-send"
              className="bg-[#0B2545] hover:bg-[#08192F] disabled:bg-gray-300 text-white rounded-full px-4 py-2 text-sm font-semibold"
            >
              ➤
            </button>
          </div>
        </div>
      )}
    </>
  );
}
